import React, { useCallback, useState } from 'react';

import { Sources } from '../generator/types';

import { BlockCard } from './BlockCard';
import { getOrderedSources, resolveBlockSet } from './blockResolver';
import { LetterBlock } from './blocks';
import { colors } from './drawerStyles';
import { LetterDrawerContextValue } from './types';

export interface BlockStackProps {
  drawer: LetterDrawerContextValue;
}

export const BlockStack = ({ drawer }: BlockStackProps) => {
  const blockSet = resolveBlockSet(drawer.letterType, drawer.pricingMode);
  const orderedSourceIds = getOrderedSources(blockSet, drawer.selectedSources);
  const blocks: LetterBlock[] = orderedSourceIds
    .map(id => blockSet[id])
    .filter((b): b is LetterBlock => b !== undefined);

  const [dragSrc, setDragSrc] = useState<Sources | null>(null);
  const [dragOver, setDragOver] = useState<Sources | null>(null);

  const handleDragOver = useCallback((id: Sources) => {
    // Only track which card is currently hovered; defer the actual reorder
    // until drop so the list doesn't churn under the cursor while dragging.
    setDragOver(prev => (prev === id ? prev : id));
  }, []);

  const handleDragEnd = useCallback(() => {
    if (dragSrc && dragOver && dragSrc !== dragOver) {
      const fromIdx = orderedSourceIds.indexOf(dragSrc);
      const toIdx = orderedSourceIds.indexOf(dragOver);
      if (fromIdx !== -1 && toIdx !== -1) {
        drawer.reorderSources(fromIdx, toIdx);
      }
    }
    setDragSrc(null);
    setDragOver(null);
  }, [dragSrc, dragOver, drawer, orderedSourceIds]);

  if (
    blocks.length === 0 &&
    drawer.selectedRiders.length === 0 &&
    !drawer.fcraEnabled
  ) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          color: colors.text3,
          fontSize: 12.5,
          padding: '30px 0',
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            border: `1.5px dashed ${colors.borderStrong}`,
            borderRadius: 7,
          }}
        />
        <p>Select sources on the left to build the letter</p>
      </div>
    );
  }

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        padding: '16px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      {blocks.map((block, idx) => (
        <BlockCard
          key={block.id}
          block={block}
          index={idx}
          isJuvenile={drawer.isJuvenile}
          fieldValues={drawer.fieldValues}
          riders={drawer.selectedRiders}
          carrierDisplayName={drawer.carrierDisplayName}
          onFieldChange={drawer.setFieldValue}
          onRemove={() => drawer.toggleSource(block.id)}
          onDragStart={id => setDragSrc(id)}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          isDraggingOver={dragOver === block.id && dragSrc !== block.id}
          isDragSrc={dragSrc === block.id}
        />
      ))}
      {drawer.selectedRiders.length > 0 ? (
        <div
          style={{
            background: colors.purpleBg,
            border: `1px solid ${colors.purpleBorder}`,
            borderRadius: 7,
            padding: '8px 11px',
            fontSize: 12,
            color: colors.purpleText,
          }}
        >
          <strong>
            Declined rider{drawer.selectedRiders.length > 1 ? 's' : ''}:
          </strong>{' '}
          {drawer.selectedRiders.join(' · ')}
        </div>
      ) : null}
      {drawer.fcraEnabled ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            padding: '7px 10px',
            border: `1px dashed ${colors.borderStrong}`,
            borderRadius: 7,
            fontSize: 11.5,
            color: colors.text2,
            background: colors.surface,
          }}
        >
          📎 FCRA attachment will be appended
        </div>
      ) : null}
    </div>
  );
};
